// /join/:slug — resolver for a group's canonical join link (slug derived
// from the group name). An active link drops the visitor straight onto the
// group's own public page — the page's normal Sign-in-to-Join /
// Request-to-Join CTAs take it from there — after seeding the `signup_ref`
// attribution cookie, which /auth/finishing claims to (a) attribute the
// signup and (b) auto-submit the join through the joinPolicy-dispatched
// endpoint.
//
// The redirect is conditional on `preview.viewerCanOpen`. For a private or
// Network-only group the group page 404s the visitor, so redirecting there
// made the leader's published link look broken — the exact "we sent the
// referral link and the Network doesn't show up" report. Those viewers get
// `JoinLinkLanding` instead, which carries the same join CTAs on this page.
//
// The remaining states render here too: an unknown code and a retired link
// each get a precise explanation instead of a 404.

import { useEffect } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Button, Container, EmptyState, LoadingState } from '@leanin/ui';
import { groupKeys } from '@/lib/queries/groups';
import { useSession } from '@/lib/use-session';
import { navigateArgsForGroup } from '@/lib/group-url';
import { isValidReferralCode, storeSignupRef } from '@/lib/signup-ref-cookie';
import { JoinLinkLanding } from '@/components/groups/JoinLinkLanding';

export const Route = createFileRoute('/_public/join/$slug')({
  component: JoinLinkResolverPage,
});

function JoinLinkResolverPage() {
  const { slug } = Route.useParams();
  const navigate = useNavigate();
  const { data: ctx } = useSession();
  const validSlug = isValidReferralCode(slug);
  const preview = useQuery({
    ...groupKeys.joinLinkPreview(slug),
    enabled: validSlug,
  });

  const signedIn = Boolean(ctx);
  const active = preview.data?.status === 'active' ? preview.data : null;
  useEffect(() => {
    if (active === null) return;
    // Cookie before redirect: it survives the signup + OAuth round-trip and
    // is what ties the eventual signup back to this link. Signed-out
    // visitors only — a signed-in member clicking the link must not leave a
    // 30-day cookie that the NEXT signup on a shared browser would claim
    // (post-claim that wouldn't just mis-tag attribution, it would
    // auto-submit a join for the wrong person).
    if (!signedIn) storeSignupRef(slug);
    if (!active.viewerCanOpen) return;
    void navigate({
      ...navigateArgsForGroup(
        active.group.kind,
        active.group.id,
        active.group.slug,
      ),
      replace: true,
    });
  }, [active, slug, navigate, signedIn]);

  if (!validSlug || (preview.isSuccess && preview.data === null)) {
    return (
      <Container max="sm">
        <EmptyState
          density="page"
          icon="link"
          title="This join link isn’t valid"
          body="Check the link for typos, or ask the person who shared it to send it again."
          primary={
            <Button variant="primary" onClick={() => void navigate({ to: '/' })}>
              Explore Lean In
            </Button>
          }
        />
      </Container>
    );
  }

  if (preview.isError) {
    return (
      <Container max="sm">
        <EmptyState
          density="page"
          icon="link"
          title="Something went wrong"
          body="We couldn’t load this join link. Try refreshing the page."
        />
      </Container>
    );
  }

  if (preview.data?.status === 'inactive') {
    return (
      <Container max="sm">
        <EmptyState
          density="page"
          icon="link"
          title="This join link is no longer active"
          body={`The link to ${preview.data.group.name} has been turned off. Ask the group’s leaders for a new one.`}
          primary={
            <Button variant="primary" onClick={() => void navigate({ to: '/' })}>
              Explore Lean In
            </Button>
          }
        />
      </Container>
    );
  }

  // Active but the group's own page would 404 this viewer (private or
  // Network-only): the join CTAs live here instead of one redirect later.
  if (active !== null && !active.viewerCanOpen) {
    return <JoinLinkLanding target={active.group} signedIn={signedIn} />;
  }

  // Pending, or active and about to redirect.
  return <LoadingState placement="page" label="Loading…" />;
}
